import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Trash2, Pencil, Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPES = [
  { value: 'comptant',    label: 'Comptant',    bg: '#F0FDF4', color: '#15803D' },
  { value: 'fin_de_mois', label: 'Fin de mois', bg: '#FFF7ED', color: '#C2410C' },
  { value: 'net',         label: 'Net',         bg: '#EFF6FF', color: '#1D4ED8' },
];

const EMPTY_FORM = { nom: '', nb_jours: 30, type_echeance: 'net' };

// ─── Petits composants ────────────────────────────────────────────────────────

function TypeBadge({ type }) {
  const cfg = TYPES.find(t => t.value === type) || TYPES[2];
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

// ─── Formulaire inline ────────────────────────────────────────────────────────

function ConditionForm({ initial, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.nom.trim().length > 0 && form.nb_jours >= 0;

  return (
    <div className="rounded-2xl border p-4 space-y-3"
      style={{ borderColor: 'var(--primary-solid)', background: '#FAFBFF' }}>
      <div className="grid grid-cols-[1fr_90px_140px] gap-3">
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Libellé *</label>
          <input autoFocus value={form.nom}
            onChange={e => set('nom', e.target.value)}
            placeholder="ex: 30 jours fin de mois"
            data-testid="cp-form-nom"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none focus:ring-2"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Jours</label>
          <input type="number" min="0" max="365"
            value={form.nb_jours}
            onChange={e => set('nb_jours', parseInt(e.target.value) || 0)}
            data-testid="cp-form-jours"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none"
            style={{ borderColor: 'var(--border)' }}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Type</label>
          <select value={form.type_echeance} onChange={e => set('type_echeance', e.target.value)}
            data-testid="cp-form-type"
            className="w-full px-3 py-1.5 rounded-lg border text-sm outline-none bg-white"
            style={{ borderColor: 'var(--border)' }}>
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm border hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <X size={13} />Annuler
        </button>
        <button type="button" onClick={() => onSave(form)} disabled={!valid || saving}
          data-testid="cp-form-save"
          className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'var(--primary-gradient)' }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function ConditionsPaiement() {
  const [conditions, setConditions] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [editId,     setEditId]     = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [deleting,   setDeleting]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getConditionsPaiementReferentiel();
      setConditions(r.data || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── CRUD ────────────────────────────────────────────────────────────────────
  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await api.createConditionPaiement(form);
      toast.success('Condition créée');
      setShowCreate(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur création');
    } finally { setSaving(false); }
  };

  const handleUpdate = async (id, form) => {
    setSaving(true);
    try {
      await api.updateConditionPaiement(id, form);
      toast.success('Condition mise à jour');
      setEditId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await api.deleteConditionPaiement(id);
      toast.success('Condition supprimée');
      await load();
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(null); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 max-w-2xl">

      {/* Header */}
      <div>
        <h2 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
          Conditions de paiement
        </h2>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Référentiel Bedee. Ces conditions sont proposées dans la fiche client, par agence.
          La date d'échéance des factures Pennylane est calculée automatiquement.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => { setShowCreate(true); setEditId(null); }}
          disabled={showCreate}
          data-testid="cp-new-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
          style={{ background: 'var(--primary-gradient)' }}>
          <Plus size={14} />Nouvelle condition
        </button>

        <button onClick={load} data-testid="cp-refresh-btn"
          className="p-2.5 rounded-xl border transition-colors hover:bg-gray-50"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
          <RefreshCw size={14} />
        </button>

        {conditions.length > 0 && (
          <span className="text-xs ml-auto" style={{ color: 'var(--text-muted)' }}>
            {conditions.length} condition{conditions.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Formulaire création */}
      {showCreate && (
        <ConditionForm
          onSave={handleCreate}
          onCancel={() => setShowCreate(false)}
          saving={saving}
        />
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : conditions.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center"
          style={{ borderColor: 'var(--border)' }}>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Aucune condition — créez-en une pour commencer.
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          {/* En-tête */}
          <div className="grid grid-cols-[1fr_80px_130px_64px] gap-3 px-4 py-2.5 border-b text-xs font-semibold uppercase tracking-wide"
            style={{ borderColor: 'var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
            <span>Libellé</span>
            <span className="text-center">Durée</span>
            <span>Type</span>
            <span />
          </div>

          {/* Lignes */}
          {conditions.map(c => (
            <div key={c.condition_id} className="border-b last:border-b-0"
              style={{ borderColor: 'var(--border-light)' }}>
              {editId === c.condition_id ? (
                <div className="px-4 py-3">
                  <ConditionForm
                    initial={c}
                    onSave={form => handleUpdate(c.condition_id, form)}
                    onCancel={() => setEditId(null)}
                    saving={saving}
                  />
                </div>
              ) : (
                <div data-testid={`cp-row-${c.condition_id}`}
                  className="grid grid-cols-[1fr_80px_130px_64px] gap-3 items-center px-4 py-3 hover:bg-gray-50/40 transition-colors">
                  <span className="text-sm font-medium truncate" style={{ color: 'var(--text)' }}>
                    {c.nom}
                  </span>
                  <span className="text-center text-sm font-mono tabular-nums"
                    style={{ color: 'var(--text-muted)' }}>
                    {c.nb_jours === 0 ? '—' : `${c.nb_jours}j`}
                  </span>
                  <TypeBadge type={c.type_echeance} />
                  <div className="flex items-center gap-1 justify-end">
                    <button
                      onClick={() => { setEditId(c.condition_id); setShowCreate(false); }}
                      data-testid={`cp-edit-${c.condition_id}`}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                      style={{ color: 'var(--text-muted)' }}>
                      <Pencil size={13} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.condition_id)}
                      disabled={deleting === c.condition_id}
                      data-testid={`cp-delete-${c.condition_id}`}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      style={{ color: '#ef4444' }}>
                      {deleting === c.condition_id
                        ? <Loader2 size={13} className="animate-spin" />
                        : <Trash2 size={13} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
